import React from 'react';

import { useParams } from 'react-router-dom';

import { reverse } from 'named-urls';

import { Divider, IconButton } from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { observer } from 'mobx-react';

import MainHeader from 'components/MainHeader';
import CustomModal from 'components/CustomModal';
import { Button } from 'components/Button';

import { useStore } from 'utils/hooks/store';

import routes from 'core/routes';

import { UserRole } from 'core/constants';

import { colors } from 'theme/colors';

import {
  MainInformation,
  MainInformationColumn,
  UserName,
  MainInformationTitle,
  ButtonWrapper,
  StyledPopover,
  MenuItem,
  InactiveBanner,
  InactiveBannerMessage,
  InactiveBannerText,
  InactiveBannerTitle,
  InactiveBannerBody,
} from './styles';

const UserProfileHeader: React.FunctionComponent<{
  setDeletionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
}> = ({ setDeletionModalOpen, anchorEl, setAnchorEl }): React.ReactElement => {
  const { userDetails, session } = useStore();
  const params = useParams<{ userId: string }>();

  const userId = params.userId as string;
  const userEmail = userDetails.details?.email ?? '';
  const isOwnProfile = !!session.user && session.user.uuid === userId;
  const userName = userDetails.details ? userDetails.details.name : 'N/A';
  const canManageUsers = session.user?.role !== UserRole.User;

  const [passwordResetModalOpen, setPasswordResetModalOpen] = React.useState(false);
  const [resendInviteModalOpen, setResendInviteModalOpen] = React.useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = React.useState(false);
  const [reactivating, setReactivating] = React.useState(false);

  const isActive = userDetails.details?.isActive ?? true;
  const canReactivate = session.user?.role === UserRole.Admin || session.user?.role === UserRole.AccountManager;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSendPasswordReset = async (): Promise<void> => {
    setPasswordResetModalOpen(false);
    await userDetails.sendPasswordReset(userId, userEmail);
  };

  const handleResendInvite = async (): Promise<void> => {
    setResendInviteModalOpen(false);
    await userDetails.resendInvite(userId, userEmail);
  };

  const handleReactivate = async (): Promise<void> => {
    setReactivating(true);
    await userDetails.reactivateUser(userId);
    setReactivating(false);
    setReactivateModalOpen(false);
  };

  return (
    <>
      <CustomModal
        openModal={passwordResetModalOpen}
        onClose={() => setPasswordResetModalOpen(false)}
        modalTitle="Send password reset email"
        modalWidth="sm"
        cancelButton={
          <Button
            onClick={() => setPasswordResetModalOpen(false)}
            id="cancel-password-reset-button"
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
        }
        submitButton={
          <Button onClick={handleSendPasswordReset} id="confirm-password-reset-button" variant="contained" size="large">
            Send reset email
          </Button>
        }
      >
        {`We'll email ${userEmail} a link to set a new password. Their current password keeps working until they use it.`}
      </CustomModal>

      <CustomModal
        openModal={resendInviteModalOpen}
        onClose={() => setResendInviteModalOpen(false)}
        modalTitle="Resend invitation"
        modalWidth="sm"
        cancelButton={
          <Button
            onClick={() => setResendInviteModalOpen(false)}
            id="cancel-resend-invite-button"
            variant="outlined"
            size="large"
          >
            Cancel
          </Button>
        }
        submitButton={
          <Button onClick={handleResendInvite} id="confirm-resend-invite-button" variant="contained" size="large">
            Resend invitation
          </Button>
        }
      >
        {`We'll resend the setup email to ${userEmail} so they can set a password and sign in.`}
      </CustomModal>

      <CustomModal
        openModal={reactivateModalOpen}
        onClose={() => setReactivateModalOpen(false)}
        modalTitle="Reactivate user"
        modalWidth="sm"
        modalDescription={`${userName} will be able to sign in again straight away. Their product licences, phone number and device assignments weren't kept — you'll need to set those up again.`}
        cancelButton={
          <Button
            onClick={() => setReactivateModalOpen(false)}
            id="cancel-reactivate-button"
            variant="outlined"
            size="large"
            disabled={reactivating}
          >
            Cancel
          </Button>
        }
        submitButton={
          <Button
            onClick={handleReactivate}
            id="confirm-reactivate-button"
            variant="contained"
            size="large"
            disabled={reactivating}
          >
            Reactivate user
          </Button>
        }
      />

      <MainHeader
        title={userName}
        documentTitle="User profile"
        breadcrumbs={
          isOwnProfile
            ? [
                { label: 'Personal', link: '' },
                { label: 'Profile', link: reverse(routes.dashboard.settings.users.details, { userId }) },
              ]
            : [
                { label: 'Organisation', link: '' },
                { label: 'Users', link: routes.dashboard.settings.users.toString() },
                { label: userName, link: '' },
              ]
        }
        action={
          canManageUsers ? (
            <>
              <ButtonWrapper>
                <IconButton onClick={handleClick}>
                  <MoreVertIcon htmlColor={colors.text.secondary} />
                </IconButton>
              </ButtonWrapper>
              <StyledPopover
                id="popover-deactivate-user"
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                {isActive ? (
                  <>
                    <MenuItem
                      id="send-password-reset-button"
                      onClick={() => {
                        setAnchorEl(null);
                        setPasswordResetModalOpen(true);
                      }}
                    >
                      Send password reset email
                    </MenuItem>
                    <MenuItem
                      id="resend-invite-button"
                      onClick={() => {
                        setAnchorEl(null);
                        setResendInviteModalOpen(true);
                      }}
                    >
                      Resend invitation
                    </MenuItem>
                    <MenuItem id="deactivate-button" onClick={() => setDeletionModalOpen(true)}>
                      Deactivate user
                    </MenuItem>
                  </>
                ) : (
                  canReactivate && (
                    <MenuItem
                      id="reactivate-button"
                      onClick={() => {
                        setAnchorEl(null);
                        setReactivateModalOpen(true);
                      }}
                    >
                      Reactivate user
                    </MenuItem>
                  )
                )}
              </StyledPopover>
            </>
          ) : undefined
        }
      />

      {!isActive && (
        <InactiveBanner>
          <InactiveBannerMessage>
            <ErrorOutlineIcon htmlColor={colors.warning.main} />
            <InactiveBannerText>
              <InactiveBannerTitle>This user is inactive</InactiveBannerTitle>
              <InactiveBannerBody>{`${userName} can't sign in until reactivated.`}</InactiveBannerBody>
            </InactiveBannerText>
          </InactiveBannerMessage>
          {canReactivate && (
            <Button
              id="reactivate-user-banner-button"
              variant="outlined"
              size="medium"
              onClick={() => setReactivateModalOpen(true)}
            >
              Reactivate user
            </Button>
          )}
        </InactiveBanner>
      )}

      <MainInformation>
        {userDetails.details?.name ? <UserName>{userDetails.details.name}</UserName> : <h1>-</h1>}
        <Divider orientation="vertical" flexItem sx={{ margin: '0 30px' }} />
        <MainInformationColumn>
          <MainInformationTitle>Email</MainInformationTitle>
          {userDetails.details?.email ? <p>{userDetails.details?.email}</p> : <p>N/A</p>}
        </MainInformationColumn>
        <MainInformationColumn>
          <MainInformationTitle>Account</MainInformationTitle>
          {userDetails.details?.organisation ? <p>{userDetails.details?.organisation.name}</p> : <p>N/A</p>}
        </MainInformationColumn>
      </MainInformation>
    </>
  );
};

export default observer(UserProfileHeader);
